import { type JSX, memo } from 'react';
import clsx from 'clsx';

import { ICON_SIZE_SIDEBAR, ICON_STROKE_WIDTH_DEFAULT } from '@/constants/ui';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { CollapsibleTabs } from '../CollapsibleTabs';
import type { SubNavLayoutProps, SubNavItem } from './SubNavLayout.types';
import styles from './SubNavLayout.module.css';

/** Reusable sidebar nav item — exported for use in SectionSidebar */
export function NavItem({
  item,
  isActive,
  onClick,
}: {
  item: SubNavItem;
  isActive: boolean;
  onClick: (id: string) => void;
}): JSX.Element {
  const Icon = item.icon;

  return (
    <button
      type="button"
      className={clsx(styles.navItem, isActive && styles.navItemActive)}
      onClick={() => onClick(item.id)}
    >
      <span className={styles.navItemIcon}>
        <Icon
          size={ICON_SIZE_SIDEBAR}
          strokeWidth={ICON_STROKE_WIDTH_DEFAULT}
        />
      </span>
      {item.label}
      {item.indicator && (
        <span className={styles.navItemIndicator} aria-hidden />
      )}
    </button>
  );
}

/**
 * SubNavLayout Component
 *
 * A responsive layout providing sidebar navigation on desktop (>= 768px)
 * and horizontal scrollable tabs on mobile (< 768px).
 *
 * @example
 * ```tsx
 * const NAV_ITEMS: SubNavItem[] = [
 *   { id: 'general', label: 'General', icon: Settings },
 *   { id: 'members', label: 'Members', icon: Users },
 * ];
 *
 * <SubNavLayout
 *   title="Settings"
 *   subtitle="Manage your workspace"
 *   items={NAV_ITEMS}
 *   activeItem={activeSection}
 *   onItemChange={setActiveSection}
 * >
 *   <SettingsContent section={activeSection} />
 * </SubNavLayout>
 * ```
 */
export const SubNavLayout = memo(function SubNavLayout({
  title,
  subtitle,
  items,
  activeItem,
  onItemChange,
  children,
}: SubNavLayoutProps): JSX.Element {
  return (
    <div className={styles.layout}>
      <nav className={styles.sidebar}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <div className={styles.navItems}>
          {items.map((item) => (
            <NavItem
              key={item.id}
              item={item}
              isActive={item.id === activeItem}
              onClick={onItemChange}
            />
          ))}
        </div>
      </nav>

      <CollapsibleTabs
        items={items}
        activeItem={activeItem}
        onItemChange={onItemChange}
        className={styles.mobileTabs}
      />

      <AppScrollArea fill>
        <div className={styles.content}>{children}</div>
      </AppScrollArea>
    </div>
  );
});
